import {
  FeEmptyArea,
  FePerson,
  IdxToFeEmptyArea,
  SelectedProduct,
} from "@/store/useCoverageBetaStore";
import { boxImageId, withImageId } from "@/utils/groupedImages";
import { CompleteTaskPayloadSchemaType } from "@/utils/zod-schemas/complete-task-payload-schema";
import { values } from "lodash";

/** Planogram/aisle state needed to fill planogram_id + empty_area_to_aisle. */
export interface CoverageBetaPlanogramState {
  basePlanogramIds: string[];
  overridePlanogramIds: Record<number, string>;
  extraPlanogramIds: string[];
  /** feEmptyAreaIdx → aisle/slot index. */
  boxIdxToSlot: Record<number, number>;
}

type ReferenceSelectedProduct = Extract<
  SelectedProduct,
  { source: "reference" }
>;

/**
 * Build the coverage-beta task-completion `result` payload from store state.
 * Shared by the real submit button and the dev "trigger submit" tool so the
 * previewed payload never drifts from what is actually sent.
 */
export const buildCoverageBetaResult = (
  idxToFeEmptyArea: IdxToFeEmptyArea,
  persons: FePerson[],
  planogramState: CoverageBetaPlanogramState,
  opts?: {
    testing?: boolean;
    /**
     * The task's own (primary) image. When given, every empty area and person
     * names the image it was drawn on as `image_id`, falling back to this —
     * same as classic Coverage, so grouped-view boxes resolve to their view.
     */
    primaryImageId?: string;
  },
): CompleteTaskPayloadSchemaType["result"] => {
  const primaryImageId = opts?.primaryImageId;
  // Appended last, matching classic's `withImageId` key order.
  const withViewImage = <T extends object>(
    box: T,
    imageId: string | null | undefined,
  ): T =>
    primaryImageId
      ? withImageId(box, boxImageId({ imageId }, primaryImageId))
      : box;
  const {
    basePlanogramIds,
    overridePlanogramIds,
    extraPlanogramIds,
    boxIdxToSlot,
  } = planogramState;

  // slot/aisle index → effective planogram id (base with overrides, then extras).
  const slotToPlanogramId: Record<number, string> = {};
  basePlanogramIds.forEach((baseId, i) => {
    slotToPlanogramId[i] = overridePlanogramIds[i] ?? baseId;
  });
  extraPlanogramIds.forEach((id, i) => {
    slotToPlanogramId[basePlanogramIds.length + i] = id;
  });

  const result: CompleteTaskPayloadSchemaType["result"] = {
    deleted_empty_areas: [],
    empty_areas: [],
    empty_areas_source: [],
    empty_area_to_aisle: [],
    unblurred_persons: persons
      .filter((p) => !p.isDeleted)
      .map((p) =>
        withViewImage(
          {
            points: p.points,
            confidence: p.confidence,
            class_index: p.class_index,
          },
          p.imageId,
        ),
      ),
    product_selections: [],
    box_categories: [],
    out_of_stock_confirmations: [],
    // `ui` marks this submission as Coverage Beta's — the daily coverage-QA
    // cron (lebron's create_coverage_beta_qa_jobs) selects on it to route
    // only Beta submissions into the audit-only Beta QA flow, leaving
    // classic-UI stores on their existing real-time per-box QA untouched.
    metadata: { image_quality_ok: true, ui: "coverage_beta" },
    testing: opts?.testing ?? false,
  };

  // Backend indices are contiguous positions into `empty_areas` (0..n-1), not
  // the FE stable ids — mirrors old coverage. `deleted_empty_areas`, however,
  // still uses the original detection index (feEmptyAreaIdx for auto areas).
  let position = 0;

  values(idxToFeEmptyArea).forEach((feEmptyArea) => {
    const {
      confidence,
      class_index,
      id,
      points,
      source,
      selectedProducts,
      isDeleted,
      feEmptyAreaIdx,
      confirmedProductId,
      imageId,
    } = feEmptyArea;

    // Deleted auto detections are reported so the backend knows they were
    // rejected; human-drawn deletions simply never entered the payload.
    if (source === "auto" && isDeleted) {
      result.deleted_empty_areas.push(feEmptyAreaIdx);
    }

    // Deleted areas are excluded from the submitted empty areas / selections.
    if (!isDeleted) {
      const emptyAreaIndex = position;
      position += 1;

      // Slot lookup uses the stable feEmptyAreaIdx; arrays are written at the
      // contiguous position.
      const slot = boxIdxToSlot[feEmptyAreaIdx] ?? 0;
      const planogramId = slotToPlanogramId[slot] ?? "";

      result.empty_areas.push(
        withViewImage({ id, points, class_index, confidence }, imageId),
      );
      result.empty_areas_source.push(source);
      result.empty_area_to_aisle.push(slot);
      //
      result.product_selections.push({
        bay_index: 0,
        empty_area_index: emptyAreaIndex,
        planogram_id: planogramId,
        product_ids: selectedProducts.map((p) => p.id || ""),
      });
      result.box_categories.push(
        selectedProducts.length > 0 ? "oos_confirmed" : "empty_area",
      );

      if (selectedProducts.length > 0) {
        // product_index(es)/reference_indices only apply to reference-sourced
        // picks.
        const refProducts = selectedProducts.filter(
          (p): p is ReferenceSelectedProduct => p.source === "reference",
        );
        const productIndices = refProducts.map(
          (p) => p.referenceProductDetectionIdx,
        );
        // The confirm step overwrites the selected product, so `selectedProducts`
        // already carries the confirmed products; `identified_product_id` reports
        // the model's original detected product (before the overwrite).
        const firstRef = refProducts[0];
        result.out_of_stock_confirmations.push({
          query_empty_area_index: emptyAreaIndex,
          confirmation_product_id:
            confirmedProductId || (selectedProducts[0]?.id ?? ""),
          confirmation_product_ids: selectedProducts.map((p) => p?.id ?? ""),
          // Explicitly pin the planogram the product was selected from, rather
          // than leaning on the backend's empty_area → aisle → planogram lookup
          // (brittle, and the source of the aisle out-of-bounds). This is the
          // box's effective planogram (base, override, or extra) — the slot it
          // was bound to when the product was picked. Set for every confirmed
          // selection so the aisle fallback is never hit.
          override_planogram_id: planogramId || null,
          identified_product_id:
            firstRef?.identifiedProductId ?? selectedProducts[0]?.id ?? null,
          // Single primary index (-1 when there is no reference-sourced pick).
          product_index: productIndices[0] ?? -1,
          product_indices: productIndices,
          reference_indices: refProducts.length
            ? refProducts.map((p) => p.referenceImgIdx)
            : null,
        });
      }
    }
  });

  // Top-level override map (overrides + extras), only when non-empty.
  const overrideMap: Record<string, string> = {};
  Object.entries(overridePlanogramIds).forEach(([slot, id]) => {
    overrideMap[slot] = id;
  });
  extraPlanogramIds.forEach((id, i) => {
    overrideMap[String(basePlanogramIds.length + i)] = id;
  });
  if (Object.keys(overrideMap).length > 0) {
    result.override_planogram_ids = overrideMap;
  }

  const propagated = summarizePropagated(idxToFeEmptyArea);
  if (propagated) result.metadata.propagated = propagated;
  const propagatedAreas = propagatedAreaOrigins(idxToFeEmptyArea);
  if (propagatedAreas) result.metadata.propagated_areas = propagatedAreas;

  return result;
};

const samePoints = (a: number[][], b: number[][]) =>
  a.length === b.length &&
  a.every(([x, y], i) => x === b[i][0] && y === b[i][1]);

/** How a pre-filled area differs from the guess it was pre-filled with. */
const propagatedEdits = (area: FeEmptyArea) => {
  const origin = area.propagated!;
  const productIds = area.selectedProducts.map((p) => p.id ?? "");
  return {
    box_edited: !samePoints(area.points, origin.points),
    products_edited: productIds.join() !== origin.productIds.join(),
  };
};

/**
 * What the labeler did with the areas pre-filled by propagation: kept as-is,
 * moved/resized or re-picked products, or deleted. Undefined when none were
 * pre-filled.
 */
export const summarizePropagated = (idxToFeEmptyArea: IdxToFeEmptyArea) => {
  const areas = values(idxToFeEmptyArea).filter((a) => a.propagated);
  if (areas.length === 0) return undefined;
  const counts = { shown: areas.length, accepted: 0, edited: 0, deleted: 0 };
  areas.forEach((area) => {
    const { box_edited, products_edited } = propagatedEdits(area);
    if (area.isDeleted) counts.deleted += 1;
    else if (box_edited || products_edited) counts.edited += 1;
    else counts.accepted += 1;
  });
  return counts;
};

/**
 * For each submitted pre-filled area (by its id in `empty_areas`), the guess
 * it came from and what the labeler changed — so lebron doesn't track an area
 * again onto frames that already have it. Undefined when none were pre-filled.
 */
export const propagatedAreaOrigins = (idxToFeEmptyArea: IdxToFeEmptyArea) => {
  const areas = values(idxToFeEmptyArea).filter(
    (a) => a.propagated && !a.isDeleted,
  );
  if (areas.length === 0) return undefined;
  return Object.fromEntries(
    areas.map((area) => [
      area.id,
      {
        source_task_id: area.propagated!.sourceTaskId,
        source_area_id: area.propagated!.areaId,
        ...propagatedEdits(area),
      },
    ]),
  );
};

/** Endpoint the completed task payload is sent to (for dev display). */
export const COVERAGE_BETA_SUBMIT_ENDPOINT = {
  method: "PATCH",
  urlTemplate: "/v1/jobs/{job_id}/tasks/{task_id}/complete",
} as const;
